import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Store, Phone, MessageCircle, BadgeCheck, MapPin, Loader2 } from "lucide-react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

// Fix default Leaflet marker icons (Webpack/Vite mangles them by default).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Role-tinted markers so users can visually distinguish partner types.
const ROLE_COLORS = {
  merchant: "#7C3AED",
  business_owner: "#F59E0B",
  iprint_shop: "#06B6D4",
  food_vendor: "#EF4444",
  hostel_owner: "#8B5CF6",
  organizer: "#EC4899",
  service_provider: "#10B981",
  agent: "#0EA5E9",
};

function coloredIcon(color) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 32' width='30' height='40'>
    <path d='M12 0C5.4 0 0 5.4 0 12c0 8.5 12 20 12 20s12-11.5 12-20C24 5.4 18.6 0 12 0z' fill='${color}' stroke='white' stroke-width='1.5'/>
    <circle cx='12' cy='12' r='4.5' fill='white'/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: "icaard-partner-marker",
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -36],
  });
}

// FitBounds — auto-resize map to encompass all pins when list changes.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13, { animate: true });
    } else {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, animate: true });
    }
  }, [points, map]);
  return null;
}

/**
 * Interactive map of iCaard partners & shops. Uses Leaflet + OpenStreetMap
 * (no API key required). Falls back to a country-centred view when no pins
 * have resolvable coordinates.
 */
export default function PartnersMap({ role = "", limit = 200, height = 420 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (role) params.set("role", role);
        params.set("limit", String(limit));
        const { data } = await api.get(`/partners/directory?${params.toString()}`);
        if (!cancelled) setRows(data.rows || []);
      } catch (e) {
        if (!cancelled) toast.error(formatError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [role, limit]);

  const pins = useMemo(
    () => rows.filter((r) => typeof r.lat === "number" && typeof r.lng === "number"),
    [rows]
  );
  const orphanCount = rows.length - pins.length;

  // Default map center: Ghana centroid.
  const defaultCenter = [7.9465, -1.0232];

  if (!loading && rows.length === 0) {
    return (
      <div className="rounded-2xl border border-[#DDE8E1] bg-gradient-to-br from-white to-[#F1F7F3] p-6 sm:p-8" data-testid="partners-map-empty">
        <div className="w-11 h-11 rounded-2xl bg-[#DCEFE3] text-[#147D60] grid place-items-center"><Store size={20} /></div>
        <h3 className="mt-4 text-lg font-bold text-[#1A3038]">Local listings are growing</h3>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#60727A]">We’re adding businesses and service providers to the public map. If you run a local business, talk to us about joining iCaard.</p>
        <a href="mailto:info@icaard.com?subject=Join%20iCaard%20as%20a%20partner" className="mt-5 inline-flex min-h-11 items-center rounded-xl bg-[#147D60] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#116B52]">Become a partner</a>
      </div>
    );
  }

  if (!loading && rows.length > 0 && pins.length === 0) {
    return (
      <div className="rounded-2xl border border-[#DDE8E1] bg-white p-6" data-testid="partners-map-no-locations">
        <h3 className="text-lg font-bold text-[#1A3038]">Meet our listed partners</h3>
        <p className="mt-1 text-sm text-[#60727A]">Their map locations are being added. Contact a partner to learn more.</p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.slice(0, 6).map((partner) => (
            <article key={partner.id} className="rounded-xl border border-[#DDE8E1] bg-[#F7FAF8] p-4">
              <strong className="block text-sm text-[#1A3038]">{partner.name}</strong>
              <span className="mt-1 block text-xs text-[#147D60]">{partner.role_label}</span>
              {partner.location && <span className="mt-2 block text-xs text-[#60727A]">{partner.location}</span>}
            </article>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="partners-map-wrapper">
      <div className="rounded-2xl overflow-hidden border border-[#26263A] relative" style={{ height }}>
        {loading && (
          <div className="absolute inset-0 z-[1000] bg-[#09090F]/70 backdrop-blur-sm flex items-center justify-center text-[#A78BFA] text-sm gap-2" data-testid="map-loading">
            <Loader2 size={14} className="animate-spin" /> Loading partners…
          </div>
        )}
        <MapContainer
          center={defaultCenter}
          zoom={7}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%", background: "#13131E" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {pins.length > 0 && <FitBounds points={pins} />}
          {pins.map((p) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={coloredIcon(ROLE_COLORS[p.role] || "#7C3AED")}
            >
              <Popup>
                <div data-testid={`map-popup-${p.id}`} className="min-w-[200px]">
                  <div className="flex items-center gap-1.5">
                    <b>{p.name}</b>
                    {p.kyc_verified && <BadgeCheck size={12} style={{ color: "#10B981" }} />}
                  </div>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "#7C3AED", marginTop: 2 }}>
                    {p.role_label}
                  </div>
                  {p.tagline && <div style={{ fontSize: 11, marginTop: 4, color: "#4B5563" }}>{p.tagline}</div>}
                  {p.location && (
                    <div style={{ fontSize: 11, marginTop: 6, color: "#4B5563", display: "flex", alignItems: "center", gap: 4 }}>
                      <MapPin size={11} /> {p.location}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {p.phone && (
                      <a href={`tel:${p.phone}`} style={{ fontSize: 11, textDecoration: "none", background: "#7C3AED", color: "white", padding: "4px 8px", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Phone size={11} /> Call
                      </a>
                    )}
                    {(p.whatsapp || p.phone) && (
                      <a href={`https://wa.me/${String(p.whatsapp || p.phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, textDecoration: "none", background: "#10B981", color: "white", padding: "4px 8px", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <MessageCircle size={11} /> WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="flex items-center justify-between text-xs text-[#9CA3D4]" data-testid="map-legend">
        <div className="flex items-center gap-1.5">
          <Store size={12} className="text-[#A78BFA]" />
          <span>
            Showing <b className="text-white">{pins.length}</b> partner{pins.length === 1 ? "" : "s"} on the map
            {orphanCount > 0 && <span className="text-[#F59E0B]"> · {orphanCount} without coordinates</span>}
          </span>
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px]">
          {Object.entries(ROLE_COLORS).slice(0, 5).map(([k, c]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: c }} />
              {k.replace("_", " ")}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
